// The follower, ported from internal/wire/follow.go.
//
// This is the client half of SPEC §3's recovery rule, and it is a PORT, not a
// reimplementation: the Go type is kept in the server tree specifically so that
// the browser and the server-side tests agree on what a gap is, and the two only
// agree if this file matches it method for method.
//
// CONTINUITY IS JUDGED ON TICK AND ON NOTHING ELSE.
//
//	TICK       tick == last+1 advances. Any other forward jump is a gap.
//	DELTA      belongs to the TICK that announced it and ticks that chunk off the
//	           expected set. A DELTA ahead of the tick line means its TICK was
//	           lost, which is a gap.
//	KEYFRAME   self-contained, so it is always applied and it is what ends a gap.
//	SELF       sent on change.
//	AGGREGATE  sent every AggregateEveryTick.
//	EPHEMERAL  dropped first under backpressure.
//
// The last three are advisory: none of them is contiguous by design, so a jump in
// one is not evidence of anything and must never throw the board away.
//
// The reason TICK exists rather than judging continuity on DELTA is the case that
// motivated it: a chunk with no changes emits no DELTA, so a subscriber to a calm
// viewport would see tick 10 then tick 12 and have to assume loss, paying a
// keyframe wait every time the map went quiet. The dirty bitmap separates the
// two — absent from the bitmap means quiet, present in the bitmap with no DELTA
// means lost.
//
// Recovery needs nothing from the server: a keyframe arrives on its own every
// KeyframeEveryTicks, which is what lets the server keep no per-client baseline,
// no history buffer and no resend path.
//
// Known limit, inherited from the Go type and worth restating because the client
// is where it bites: a chunk KEYFRAME restores only its own chunk, but this type
// treats any keyframe as ending a gap. §3 says a gap is ended by a BOARD-scope
// keyframe; `Connection` enforces that above this layer by only calling
// observeKeyframe out of a gap for a board-scope frame.

import { ChunkSet } from '../wire/chunkset';
import {
  ClassAggregate,
  ClassDelta,
  ClassEphemeral,
  ClassKeyframe,
  ClassSelf,
  ClassTick,
} from '../wire/classes';
import { validChunk, type ChunkID, type TickNo } from '../wire/core';
import { errDirtyBitmap } from '../wire/errors';
import type { SubscribeDiff } from '../wire/subscribe';
import type { Tick } from '../wire/tick';

/** What the client should do with a frame. */
export const StatusOK = 'ok';
/** Older than what has already been applied, or a duplicate. Drop it. */
export const StatusStale = 'stale';
/** Frames were missed. Discard the local board and wait for the next keyframe. */
export const StatusGap = 'gap';
/** Already in gap recovery, or no board yet, and this is not a keyframe. Ignore it. */
export const StatusWaiting = 'waiting';
/** A class whose cadence is not per-tick. Apply it, but never judge continuity by it. */
export const StatusAdvisory = 'advisory';
/** TICK and DELTA carry continuity information in their bodies; the header alone cannot classify them. */
export const StatusNeedsBody = 'needs-body';

export type Status =
  | typeof StatusOK
  | typeof StatusStale
  | typeof StatusGap
  | typeof StatusWaiting
  | typeof StatusAdvisory
  | typeof StatusNeedsBody;

export class Follower {
  private subs = new ChunkSet();
  private last: TickNo = 0;
  private started = false;
  private waiting = false;
  private expected = new ChunkSet();
  private pendingCount = 0;

  /**
   * Replaces the set of chunks the follower expects DELTAs for. Outstanding
   * expectations for chunks that are no longer subscribed are dropped, so a pan
   * mid-tick cannot leave the follower waiting forever for a chunk it stopped
   * caring about.
   */
  setSubscriptions(s: ChunkSet): void {
    if (!s.canonical()) throw errDirtyBitmap();
    this.subs = s.clone();
    this.expected = this.expected.intersect(this.subs);
    this.pendingCount = this.expected.count;
  }

  /** Folds a pan diff into the subscription set. Call it with the same diff the client puts on the wire. */
  applySubscribe(d: Pick<SubscribeDiff, 'add' | 'remove'>): void {
    const next = this.subs.clone();
    next.apply(d);
    this.setSubscriptions(next);
  }

  /** The set completeness is judged against. */
  subscriptions(): ChunkSet {
    return this.subs;
  }

  /** The newest tick applied. */
  lastTick(): TickNo {
    return this.last;
  }

  /** Whether the follower is in gap recovery or has no board yet. */
  needsKeyframe(): boolean {
    return this.waiting || !this.started;
  }

  /**
   * Whether every DELTA the current tick announced for a subscribed chunk has
   * arrived. This is the render signal: a client that draws on tick completion
   * never paints half a tick.
   */
  tickComplete(): boolean {
    return this.pendingCount === 0;
  }

  /** How many announced DELTAs are still outstanding for the current tick. */
  pending(): number {
    return this.pendingCount;
  }

  /** Which chunks those are. */
  outstanding(): ChunkSet {
    return this.expected;
  }

  /** Returns the follower to its just-connected state, subscriptions kept. */
  reset(): void {
    this.last = 0;
    this.started = false;
    this.waiting = false;
    this.expected = new ChunkSet();
    this.pendingCount = 0;
  }

  /**
   * Classifies a frame from its header alone. TICK and DELTA cannot be classified
   * this way and come back as StatusNeedsBody.
   */
  observe(h: { class: number; tick: TickNo }): Status {
    switch (h.class) {
      case ClassKeyframe:
        return this.observeKeyframe(h.tick);
      case ClassTick:
      case ClassDelta:
        return StatusNeedsBody;
      case ClassSelf:
      case ClassEphemeral:
      case ClassAggregate:
        return StatusAdvisory;
      default:
        return StatusStale;
    }
  }

  /**
   * The recovery path. A keyframe is self-contained, so it is never a gap and
   * always ends one.
   */
  observeKeyframe(tick: TickNo): Status {
    if (this.started && tick < this.last) return StatusStale;
    if (!this.started || this.waiting) {
      this.last = tick;
      this.started = true;
      this.waiting = false;
      this.expected = new ChunkSet();
      this.pendingCount = 0;
      return StatusOK;
    }
    if (tick > this.last) {
      // A routine keyframe ahead of the tick line means TICKs were missed, but
      // the keyframe is the thing that would have repaired that anyway, so
      // resync to it rather than declaring a gap and waiting for another.
      //
      // This is also the ordinary case, not an exception: §3 says a keyframe
      // REPLACES that tick's boundary message and deltas for the client
      // receiving it, so a client on tick N that is handed the keyframe for
      // N+1 gets no TICK and no DELTA for N+1 and arrives here every time.
      this.last = tick;
      this.expected = new ChunkSet();
      this.pendingCount = 0;
    }
    // A keyframe for the tick already being assembled does not disturb it.
    return StatusOK;
  }

  /** The continuity check. Call it once per decoded TICK. */
  observeTick(t: Tick): Status {
    if (!this.started || this.waiting) return StatusWaiting;
    if (t.tick <= this.last) return StatusStale;
    if (t.tick === this.last + 1) {
      if (this.pendingCount > 0) {
        // The tick that is ending announced DELTAs that never arrived. That is a
        // real loss, not a quiet chunk.
        this.waiting = true;
        return StatusGap;
      }
      this.last = t.tick;
      this.expected = t.dirty.intersect(this.subs);
      this.pendingCount = this.expected.count;
      return StatusOK;
    }
    this.waiting = true;
    return StatusGap;
  }

  /** Matches a decoded DELTA against the TICK that announced it. */
  observeDelta(tick: TickNo, chunk: ChunkID): Status {
    if (!this.started || this.waiting) return StatusWaiting;
    if (!validChunk(chunk)) return StatusStale;
    if (tick < this.last) return StatusStale;
    if (tick > this.last) {
      // On one ordered socket a DELTA cannot outrun its own TICK, so its TICK was lost.
      this.waiting = true;
      return StatusGap;
    }
    if (this.expected.has(chunk)) {
      this.expected.unset(chunk);
      this.pendingCount--;
    }
    // A DELTA for a chunk the bitmap did not mark, or for one the client has just
    // unsubscribed from, is still applied: a DELTA is authoritative state and
    // dropping it would leave the board wrong. It simply counts for nothing.
    return StatusOK;
  }
}
